import React from 'react';
import { motion } from 'framer-motion';
import { FaBuilding, FaUsers, FaHandshake, FaFlask, FaMedal, FaClinicMedical, FaBaby, FaFemale, FaBone } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const About = () => {
    const fadeIn = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6 }
        }
    };

    const specialties = [
        {
            id: 'pediatrics',
            name: 'Pediatrics',
            icon: <FaBaby className="text-pediatric text-3xl" />,
            description: 'Specialized formulations for children\'s growth and well-being',
      color: 'bg-pediatric',
        link: '/products/pediatrics'
    },
    {
        id: 'gynecology',
        name: 'Gynecology',
        icon: <FaFemale className="text-gynecology text-3xl" />,
            description: 'Advanced healthcare solutions for women at every stage of life',
                color: 'bg-gynecology',
                    link: '/products/gynecology'
    },
{
    id: 'orthopedics',
        name: 'Orthopedics',
            icon: <FaBone className="text-orthopedic text-3xl" />,
                description: 'Innovative treatments for bone and joint health',
                    color: 'bg-orthopedic',
                        link: '/products/orthopedics'
}
  ];

return (
    <div>
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 py-20">
            <div className="container-custom">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    <motion.div
                        initial="hidden"
                        animate="visible"
                        variants={fadeIn}
                    >
                        <h1 className="text-4xl md:text-5xl font-bold mb-6">About Migliore Life Sciences</h1>

                        <p className="text-lg text-gray-700 mb-6">
                            Established in 2012, Migliore Life Sciences is a pharmaceutical company based in Pune, India.
                            We specialize in three core medical segments: Pediatrics, Gynecology, and Orthopedics.
                        </p>

                        <div className="flex flex-wrap gap-4 mt-8">
                            <Link to="/products" className="btn-primary">
                                Explore Our Products
                            </Link>
                            <Link to="/contact" className="btn-outline-primary">
                                Contact Us
                            </Link>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="relative"
                    >
                        <div className="relative rounded-xl overflow-hidden shadow-xl">
                            <img
                                src="https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80"
                                alt="Migliore Life Sciences Facility"
                                className="w-full h-auto"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "https://via.placeholder.com/600x400/E6F7FF/0066CC?text=Migliore+Life+Sciences";
                                }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-tr from-primary/40 to-transparent"></div>
                        </div>

                        {/* Floating stats */}
                        <motion.div
                            className="absolute -bottom-6 -left-6 bg-white rounded-lg shadow-lg p-4 flex items-center space-x-3"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6, duration: 0.6 }}
                        >
                            <div className="bg-primary/10 rounded-full p-3">
                                <FaMedal className="text-primary text-xl" />
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-primary">12+</div>
                                <div className="text-sm text-gray-600">Years of Excellence</div>
                            </div>
                        </motion.div>

                        <motion.div
                            className="absolute -top-6 -right-6 bg-white rounded-lg shadow-lg p-4 flex items-center space-x-3"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.8, duration: 0.6 }}
                        >
                            <div className="bg-secondary/10 rounded-full p-3">
                                <FaFlask className="text-secondary text-xl" />
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-secondary">28</div>
                                <div className="text-sm text-gray-600">Trusted Brands</div>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </div>

        {/* Our Story Section */}
        <section className="py-20">
            <div className="container-custom">
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Story</h2>
                    <div className="w-24 h-1 bg-primary mx-auto mb-8"></div>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        A decade of commitment to healthcare excellence and innovation
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="space-y-6"
                    >
                        <p className="text-lg text-gray-700">
                            Established in 2012, Migliore Life Sciences has grown to become a trusted name in the pharmaceutical industry in India. Our commitment to quality and innovation has led us to develop 28 trusted brands across our core specializations.
                        </p>

                        <p className="text-lg text-gray-700">
                            We are a marketing-driven pharmaceutical company focused on delivering specialized healthcare solutions that meet the specific needs of doctors and patients.
                        </p>

                        <p className="text-lg text-gray-700">
                            With 28 established brands across India, we are committed to quality-driven product development, doctor-focused engagement, and ethical and sustainable growth.
                        </p>

                        <div className="pt-4">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                                    <FaBuilding className="text-primary text-xl" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg">Corporate Office</h3>
                                    <p className="text-gray-600">Landmark Centre, Pune, India</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                                    <FaClinicMedical className="text-primary text-xl" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg">Distribution Center</h3>
                                    <p className="text-gray-600">Full-service depot at Fursungi, Pune</p>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="grid grid-cols-1 gap-6">
                            <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-primary">
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-full bg-primary/10 flex-shrink-0 flex items-center justify-center">
                                        <FaUsers className="text-primary text-xl" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-lg mb-2">Doctor-Focused Engagement</h3>
                                        <p className="text-gray-600">We prioritize strong relationships with healthcare professionals, understanding their needs to develop targeted solutions.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-secondary">
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-full bg-secondary/10 flex-shrink-0 flex items-center justify-center">
                                        <FaMedal className="text-secondary text-xl" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-lg mb-2">Quality-Driven Products</h3>
                                        <p className="text-gray-600">We maintain stringent quality standards across our entire range of pharmaceutical products, ensuring safety and efficacy.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-primary">
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-full bg-primary/10 flex-shrink-0 flex items-center justify-center">
                                        <FaHandshake className="text-primary text-xl" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-lg mb-2">Ethical & Sustainable Growth</h3>
                                        <p className="text-gray-600">We are committed to responsible business practices that benefit our partners, patients, and the environment.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>

        {/* Our Specializations */}
        <section className="py-20 bg-gray-50">
            <div className="container-custom">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Medical Specializations</h2>
                    <div className="w-24 h-1 bg-primary mx-auto mb-8"></div>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        We focus on three core therapeutic areas to deliver specialized solutions
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {specialties.map((specialty, index) => (
                        <motion.div
                            key={specialty.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.2, duration: 0.6 }}
                            className="bg-white rounded-lg shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-xl hover:-translate-y-2"
                        >
                            <div className={`h-3 ${specialty.color}`}></div>
                            <div className="p-8">
                                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-6 mx-auto">
                                    {specialty.icon}
                                </div>
                                <h3 className="text-xl font-bold mb-4 text-center">{specialty.name}</h3>
                                <p className="text-gray-600 text-center mb-6">{specialty.description}</p>
                                <Link
                                    to={specialty.link}
                                    className="block text-center py-2 border border-primary text-primary rounded-md hover:bg-primary hover:text-white transition-colors"
                                >
                                    View Products
                                </Link>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-primary to-primary-dark text-white">
            <div className="container-custom text-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Partner with Us?</h2>
                    <p className="text-xl max-w-3xl mx-auto mb-10">
                        Discover how Migliore Life Sciences can support your healthcare practice with our innovative pharmaceutical solutions.
                    </p>
                    <Link
                        to="/contact"
                        className="inline-block bg-white text-primary font-bold py-4 px-8 rounded-md shadow-lg hover:bg-gray-100 transition-colors"
                    >
                        Contact Us Today
                    </Link>
                </motion.div>
            </div>
        </section>
    </div>
);
};

export default About;
